/* app/research-lenses.jsx - the four analysis lenses (config for AnalysisModule).
   Competitors / Partners / Industry analysis + Product Enrichment. */
(function () {
  function I(props) { return React.createElement(window.MlIcon, props); }
  const relBadge = (r) => { const m = { look: ["Lookalike", "look"], comp: ["Complementary", "comp"], alt: ["Alternative", "alt"] }[r]; return <span key={r} className={"bi-badge bi-badge--" + m[1]}>{m[0]}</span>; };

  // - adapters for the live backend (BI entities → list; analysis JSON → sections) -
  const REL_IN = { lookalike: "look", complementary: "comp", alternative: "alt" };
  // `extra` carries section-level flags (today: `clustered`, so the capture
  // control can say it is banking keywords grouped into clusters).
  const rowsSec = (title, rows, extra) => (rows.length ? { title, kind: "rows", rows, ...(extra || {}) } : null);
  const chipsSec = (title, chips) => (chips.length ? { title, kind: "chips", chips } : null);
  const proseSec = (title, text) => (text ? { title, kind: "prose", text } : null);
  const disc = (r) => (r.proposed_neighbours || []).map((d, i) => ({ id: "d" + i, name: d.name, url: d.url, conf: Math.round(d.confidence || 0), sum: d.summary, rels: d.relationships_to_me || [] }));
  // One line out of several, empties dropped, so a missing field never leaves a
  // dangling separator on screen.
  const line = (...parts) => parts.filter(p => p != null && String(p).trim() !== "").join(" · ");

  // What the SITE already publishes, in the words the coverage scan is willing
  // to stand behind. The backend answers this from lib/coverage.js against the
  // last scan of the live site; the analysis never sees the site and is told not
  // to guess, so nothing here is inferred on screen either.
  //
  // "not_known" is a real answer and reads as one: a scan that never ran, or one
  // that stopped at its ceiling, cannot rule a page out, and rendering that as
  // "you have not written this" would be the same lie as a wrong "covered".
  const HOW = {
    manual       : "someone recorded it",
    pipeline     : "we published it from a plan",
    focus_keyword: "a page declares it as its focus keyword",
    title        : "it appears in a page title",
    slug         : "it appears in a page URL",
  };
  const covLine = (c) => {
    if (!c || !c.status) return "";
    if (c.status === "covered") {
      const where = (c.posts || []).map(p => p.title || p.url).filter(Boolean).slice(0, 2).join(", ");
      const how = HOW[c.matched_by] || "matched by the coverage scan";
      const more = c.posts_total > 1 ? " and " + (c.posts_total - 1) + " more page" + (c.posts_total > 2 ? "s" : "") : "";
      return "Already on your site: " + (where || c.posts_total + " page(s)") + more + " (" + how
        + (c.confidence != null ? ", confidence " + c.confidence + "%" : "") + ").";
    }
    if (c.status === "no_match") return "Nothing on your site matched this in the last scan, which is not proof it is missing.";
    return "Coverage not known: " + (c.why || "the site has not been scanned.");
  };

  // The two halves of "topic": concrete titles a writer can be briefed on, and
  // the wider themes they sit under. Both are new keys, and both carry the
  // coverage the site's own scan proved. Titles go to the topics bank and to
  // pages (a title IS a page); a theme is a topic only, because "write the
  // theme" is not a page anyone can draft.
  const titleRows = (r) => (r.article_titles || []).map((t, i) => ({
    id: "at" + i, title: t.title, intent: t.intent,
    desc: line(covLine(t.coverage), t.why, t.primary_keyword ? "Target keyword: " + t.primary_keyword : "", t.theme ? "Theme: " + t.theme : ""),
    cap: ["topic", "blog"],
    meta: { detail: line(t.why, t.primary_keyword ? "Target keyword: " + t.primary_keyword : "", t.theme ? "Theme: " + t.theme : ""), intent: t.intent },
  }));
  const themeRows = (r) => (r.topic_themes || []).map((t, i) => ({
    id: "tt" + i, title: t.theme,
    desc: line(covLine(t.coverage), t.detail, (t.subtopics || []).length ? "Subtopics: " + (t.subtopics || []).join(", ") : ""),
    cap: ["topic"],
    meta: { detail: line(t.detail, (t.subtopics || []).length ? "Subtopics: " + (t.subtopics || []).join(", ") : "") },
  }));
  // The reply hit the output cap and stopped mid-answer. The routes have always
  // reported it and no lens showed it, which was survivable while the lists were
  // short; now that they ask for every keyword and title, a list cut in half
  // would otherwise read as a complete one.
  // A truncated reply is salvaged into a valid object, so what survives looks
  // complete. It is not: on the analysis that prompted this, four whole
  // sections were gone, the gaps against the user's own site among them, under
  // a notice that said only "the lists below stop early". The server now
  // records which sections never arrived, so this says them by name.
  const SECTION_NAME = {
    content_gaps_vs_us: "the content gaps against your site", positioning: "their positioning",
    cadence: "their publishing cadence", proposed_neighbours: "the other businesses it would have suggested",
    collaboration_opportunities: "the collaboration opportunities", audience_overlap: "the audience overlap",
    content_topics: "their content topics", complementary_topics: "their complementary topics",
    article_titles: "the article titles", topic_themes: "the wider themes",
    format_mix: "their format mix", co_marketing_formats: "the co-marketing formats",
    keyword_overlap: "the keyword overlap", joint_keywords: "the joint keywords",
    long_tail_keywords: "their other keywords", summary: "the summary",
  };
  const andList = (xs) => xs.length <= 1 ? (xs[0] || "") : xs.slice(0, -1).join(", ") + " and " + xs[xs.length - 1];
  const truncSec = (r) => {
    if (!r.truncated) return null;
    const missing = (r.missing_sections || []).map(k => SECTION_NAME[k] || k);
    return proseSec("This answer is incomplete", missing.length
      ? "The model stopped at its output limit before finishing, so " + andList(missing)
        + " never arrived. What is shown below is everything that did. Re-run to get the rest."
      : "The model stopped at its output limit before finishing, so the lists below stop early rather than ending. Re-run to get the rest.");
  };

  // ── What their site IS, counted from their own sitemap ──
  // No model involved and no crawl: one sitemap read answers the questions a
  // single page cannot, and these were the questions the analysis used to
  // answer by inference. Counts and real examples only.
  const structSec = (r) => {
    const st = r.site_structure;
    if (!st) return null;
    if (!st.ok) return proseSec("How their site is built", "Their sitemap could not be read (" + (st.reason || "no sitemap") + "), so everything about their formats and cadence below comes from the one page that was read.");
    const parts = [];
    parts.push(st.total_urls.toLocaleString() + " indexed URLs" + (st.capped ? " (capped)" : "") + ".");
    if ((st.sections || []).length) {
      parts.push("Biggest sections: " + st.sections.slice(0, 6).map(x => "/" + x.path + " (" + x.count + ")").join(", ") + ".");
    }
    if ((st.languages || []).length) {
      parts.push("Published in " + st.languages.length + " languages: " + st.languages.map(l => l.code + " (" + l.count + ")").join(", ") + ".");
    }
    const c = st.cadence || {};
    if (c.known) {
      parts.push("About " + c.per_month + " URLs touched per month; newest " + c.newest + ", oldest " + c.oldest
        + ". " + c.share_touched_90d + "% of the whole site was touched in the 90 days before their last change"
        + (c.share_touched_90d >= 25 ? ", which is an active refresh habit, not just new publishing." : "."));
    }
    return proseSec("How their site is built", parts.join(" "));
  };

  // The templated page sets, which is where "programmatic SEO" actually lives.
  const setsSec = (r) => {
    const st = r.site_structure;
    if (!st || !st.ok) return null;
    const AXIS = { place: "one page per place", sector: "one page per sector", articles: "an article hub" };
    return rowsSec("Their templated page sets", (st.templated_sets || []).map((x, i) => ({
      id: "ts" + i, title: "/" + x.parent, prio: x.count >= 100 ? "high" : x.count >= 25 ? "medium" : "low",
      desc: x.count + " pages" + (x.axis ? ", " + AXIS[x.axis] : ", axis not identified")
        + ". Examples: " + (x.examples || []).slice(0, 3).join(", "),
      cap: ["todo"], meta: { detail: x.count + " pages under /" + x.parent },
    })));
  };

  // The plays themselves, each with why it exists in search.
  const playsSec = (r) => {
    const st = r.site_structure;
    if (!st || !st.ok) return null;
    return rowsSec("SEO plays they run", (st.patterns || []).map((x, i) => ({
      id: "pp" + i, title: x.label, prio: x.count >= 50 ? "high" : x.count >= 10 ? "medium" : "low",
      desc: x.count + " page" + (x.count === 1 ? "" : "s") + ". " + x.why,
      cap: ["todo"], meta: { detail: x.why, priority: x.count >= 50 ? "high" : "medium" },
    })));
  };

  // Their vocabulary, counted. NOT search volume, and it says so.
  const vocabSec = (r) => {
    const st = r.site_structure;
    if (!st || !st.ok || !(st.vocabulary || []).length) return null;
    return rowsSec("The vocabulary their site is built on", st.vocabulary.map((v, i) => ({
      id: "vv" + i, title: v.term, desc: "Appears in " + v.count + " of their URLs and titles. This is how often THEY use the term, not how often anyone searches it.",
      cap: ["keyword"], meta: { note: "counted in " + v.count + " of their own URLs and titles" },
    })));
  };

  // Every article they publish, by title. Read off the URL, which is said
  // plainly: the wording is close, not exact.
  const theirTitlesSec = (r) => {
    const st = r.site_structure;
    if (!st || !st.ok || !(st.titles || []).length) return null;
    const more = (st.titles_total || st.titles.length) - st.titles.length;
    return rowsSec("Everything they have published" + (more > 0 ? " (first " + st.titles.length + " of " + st.titles_total + ")" : " (" + st.titles.length + ")"),
      st.titles.map((t, i) => ({
        id: "tt" + i, title: t.title,
        desc: (t.lastmod ? "Last changed " + String(t.lastmod).slice(0, 10) + ". " : "") + "Title read from the URL, so the wording is approximate.",
        cap: ["topic"], meta: { detail: "published by them" },
      })));
  };

  // Where the answer came from, on the screen that shows it. An analysis saved
  // before these keys existed carries neither, and proseSec drops an empty
  // section, so nothing is claimed for a run that never reported it.
  const covNote = (r) => {
    const c = r.coverage;
    if (!c) return "";
    return line(c.note, c.checked
      ? c.covered + " of " + c.checked + " already on your site, " + c.no_match + " with no match, " + c.not_known + " not known"
      : "", c.verdict);
  };
  const evidenceNote = (r) => {
    const e = r.evidence;
    if (!e) return "";
    const out = [];
    const n = e.web_searches || 0;
    if (e.web_search === "used") {
      out.push("Web search: " + n + " live search" + (n === 1 ? "" : "es") + " ran"
        + (e.web_country ? " from " + e.web_country : "") + "."
        + ((e.web_queries || []).length ? " Queries: " + e.web_queries.slice(0, 5).join("; ") + "." : ""));
    } else if (e.web_search === "requested_but_unused") {
      out.push("Web search: the tool was attached and the model never used it, so this answer is its own knowledge, not live results.");
    } else if (e.web_search === "off_for_this_request") {
      out.push("Web search: off for this run, so this answer is the model's own knowledge.");
    } else if (e.web_search === "disabled_for_deployment") {
      out.push("Web search: disabled on this deployment, so this answer is the model's own knowledge.");
    }
    const g = e.gsc || {};
    if (g.error) out.push("Search Console: could not be read (" + g.error + "), so no first-party query data was used.");
    else if (!g.connected) out.push("Search Console: not connected, so no first-party query data was used.");
    else if (g.used) out.push("Search Console: " + g.queries + " real queries from the last " + g.window_days + " days were used as evidence.");
    else out.push("Search Console: connected, but no query rows are stored for the last " + g.window_days + " days.");
    return out.join(" ");
  };
  // The exhaustive keyword list, kept apart from the overlap/joint lists rather
  // than swelling them: those two are defined by a relation to the user, this
  // one is everything the target visibly goes after. The cluster travels with
  // each keyword, which is the only way keywords.cluster is ever written.
  const longTailRows = (r) => (r.long_tail_keywords || []).map((k, i) => ({
    id: "lt" + i, title: k.keyword, intent: k.intent, desc: line(k.cluster ? "Cluster: " + k.cluster : "", k.note),
    cap: ["keyword"], meta: { cluster: k.cluster, intent: k.intent, note: k.note },
  }));

  // `meta` is the facets the analysis returned for a row, named as the capture
  // API stores them: keyword takes intent/note/cluster/priority, topic
  // detail/intent, page and to-do detail, faq answer. They used to be shown on
  // screen and then thrown away at the button, which left every captured
  // keyword bare and made classification re-derive from a string what the
  // analysis already knew. Keys are only present when the analysis produced
  // them; nothing is invented. The same `meta` travels whichever bank a row is
  // sent to and the server keeps what that bank has a column for: a gap's
  // priority reaches todos, and is dropped for topics and content_pages
  // because neither table has the column. That is the schema, not a loss.

  // ---------- Competitors Analysis ----------
  const COMPETITORS = {
    title: "Competitors Analysis", plural: "competitors", noun: "analysis", icon: "flag",
    verb: "analyse", verbing: "Analysing", verbed: "analysed", reverb: "Re-analyse", batchVerb: "Analyse", biLabel: "Competitors",
    intro: <>The <b>rivalry lens</b>. Pick a tracked competitor, run an AI analysis of their content &amp; SEO, and capture gaps and topics into your workflow. Reads from <b>BI · Competitors</b>.</>,
    emptyDesc: "No competitors in BI yet. Add them in BI · Competitors first, then analyse them here.",
    neverDesc: "Run a content & SEO analysis of this competitor to surface gaps, topics, keyword overlap and their positioning.",
    discovers: true,
    entityType: "competitor", endpoint: "competitor-analysis",
    mapEntity: (row) => { const d = row.data || {}; return { id: row.id, name: row.name, rels: (d.relationships_to_me || []).map(r => REL_IN[r]).filter(Boolean), meta: [["Market", d.market || "-"], ["Positioning", d.positioning || "-"]] }; },
    mapResult: (r) => ({
      summary: r.summary || "",
      sections: [
        truncSec(r),
        rowsSec("Content gaps vs us", (r.content_gaps_vs_us || []).map((g, i) => ({ id: "g" + i, title: g.gap, prio: g.priority, desc: g.opportunity, cap: ["topic", "page"], meta: { detail: g.opportunity, priority: g.priority } }))),
        rowsSec("Content topics", (r.content_topics || []).map((t, i) => ({ id: "t" + i, title: t.topic, desc: t.detail, cap: ["topic", "blog"], meta: { detail: t.detail } }))),
        rowsSec("Article titles", titleRows(r)),
        rowsSec("Wider themes", themeRows(r)),
        chipsSec("Format mix", (r.format_mix || []).map(f => ({ b: f.prevalence, t: f.format }))),
        rowsSec("Keyword overlap", (r.keyword_overlap || []).map((k, i) => ({ id: "k" + i, title: k.keyword, intent: k.intent, desc: k.note, cap: ["keyword"], meta: { intent: k.intent, note: k.note } }))),
        rowsSec("Every other keyword they target", longTailRows(r)),
        proseSec("Positioning", r.positioning), proseSec("Publishing cadence", r.cadence),
        structSec(r), setsSec(r), playsSec(r), vocabSec(r), theirTitlesSec(r),
        proseSec("What your site already covers", covNote(r)),
      ].filter(Boolean),
      discovered: disc(r),
    }),
    stats: [["tracked", "Tracked", "var(--primary)", 6], ["rels", "With relationships", "var(--blue-500)", 4], ["analysed", "Analysed", "var(--success)", 3]],
    statFilter: (s, e) => s === "rels" ? e.rels && e.rels.length : s === "analysed" ? !!e.analysed : true,
    cardBadges: (e) => <>{(e.rels || []).map(relBadge)}</>,    makeResult: (e) => ({
      summary: e.name + " publishes high-cadence, bottom-of-funnel comparison content and ranks for several clusters you don't yet cover. Biggest opportunity: programmatic landing pages.",
      sections: [
        { title: "Content gaps vs us", kind: "rows", rows: [
          { id: "g1", title: "Programmatic “best X tool” pages", prio: "high", desc: "They have 40+ templated comparison pages; you have none.", cap: ["topic", "page"] },
          { id: "g2", title: "Free tools as lead magnets", prio: "medium", desc: "Free SERP checker drives links and signups.", cap: ["topic", "todo"] },
          { id: "g3", title: "Glossary / definitions hub", prio: "low", desc: "Captures top-of-funnel informational intent.", cap: ["topic"] },
        ] },
        { title: "Content topics", kind: "rows", rows: [
          { id: "t1", title: "Keyword clustering workflows", desc: "Recurring theme across their blog.", cap: ["topic", "blog"] },
          { id: "t2", title: "Technical SEO audits at scale", desc: "Strong engagement signals.", cap: ["topic", "blog"] },
        ] },
        { title: "Format mix", kind: "chips", chips: [{ b: "48%", t: "How-to guides" }, { b: "22%", t: "Comparisons" }, { b: "18%", t: "Listicles" }, { b: "12%", t: "Case studies" }] },
        { title: "Keyword overlap", kind: "rows", rows: [
          { id: "k1", title: "content audit tool", intent: "Commercial", desc: "They rank #3; you rank #14.", cap: ["keyword"] },
          { id: "k2", title: "seo content brief", intent: "Informational", desc: "They rank #5; you're unranked.", cap: ["keyword"] },
        ] },
        { title: "Positioning", kind: "prose", text: "Positioned as the all-in-one toolkit for SEO teams. Messaging leans on data depth and breadth over ease of use - an opening for a simpler, operator-focused angle." },
        { title: "Publishing cadence", kind: "prose", text: "Roughly 9 posts per week across blog and academy, with heavy internal linking into product pages." },
      ],
      discovered: [
        { id: "d1", name: "Frase", conf: 82, sum: "AI content briefs - overlaps with their content workflow content." },
        { id: "d2", name: "Outranking", conf: 64, sum: "SERP-driven generation; appears in their comparison pages." },
      ],
    }),
  };

  // ---------- Partners Analysis ----------
  const PARTNERS = {
    title: "Partners Analysis", plural: "partners", noun: "analysis", icon: "git-branch",
    verb: "analyse", verbing: "Analysing", verbed: "analysed", reverb: "Re-analyse", batchVerb: "Analyse", biLabel: "Partners",
    intro: <>The <b>collaboration lens</b>. Find complementary topics, co-marketing angles and joint keywords with a tracked partner. Reads from <b>BI · Partners</b>.</>,
    emptyDesc: "No partners in BI yet. Add them in BI · Partners first, then analyse them here.",
    neverDesc: "Analyse this partner to surface complementary topics, co-marketing angles and keywords you could target together.",
    discovers: true,
    entityType: "partner", endpoint: "partner-analysis",
    mapEntity: (row) => { const d = row.data || {}; return { id: row.id, name: row.name, active: d.status === "active", meta: [["Type", d.partner_type || "-"], ["Services", d.services || "-"]] }; },
    mapResult: (r) => ({
      summary: r.summary || "",
      sections: [
        truncSec(r),
        rowsSec("Complementary topics", (r.complementary_topics || []).map((t, i) => ({ id: "c" + i, title: t.topic, desc: t.detail, cap: ["topic", "blog"], meta: { detail: t.detail } }))),
        rowsSec("Article titles", titleRows(r)),
        rowsSec("Wider themes", themeRows(r)),
        rowsSec("Co-marketing angles", (r.collaboration_opportunities || []).map((a, i) => ({ id: "a" + i, title: a.opportunity, prio: a.priority, desc: a.detail, cap: ["todo", "page"], meta: { detail: a.detail, priority: a.priority } }))),
        // The partner twin of the competitor "Format mix": the analysis has
        // always returned it and the screen never showed it.
        chipsSec("Co-marketing formats", (r.co_marketing_formats || []).map(f => ({ b: f.prevalence, t: f.format }))),
        rowsSec("Joint keywords", (r.joint_keywords || []).map((k, i) => ({ id: "jk" + i, title: k.keyword, intent: k.intent, desc: k.note, cap: ["keyword"], meta: { intent: k.intent, note: k.note } }))),
        rowsSec("Every other keyword they target", longTailRows(r)),
        proseSec("Positioning", r.positioning), proseSec("Audience overlap", r.audience_overlap),
        // The same counted read of their site as the competitor lens. For a
        // partner it answers a different question: what surface they already
        // run that something could be co-published on.
        structSec(r), setsSec(r), playsSec(r), vocabSec(r), theirTitlesSec(r),
        proseSec("What your site already covers", covNote(r)),
      ].filter(Boolean),
      discovered: disc(r),
    }),
    stats: [["tracked", "Tracked", "var(--primary)", 4], ["active", "Active", "var(--success)", 2], ["analysed", "Analysed", "var(--blue-500)", 1]],
    statFilter: (s, e) => s === "active" ? e.active : s === "analysed" ? !!e.analysed : true,
    cardBadges: (e) => <>{e.active && <span className="bi-badge bi-badge--validated"><I name="check" size={11} />Active</span>}</>,    makeResult: (e) => ({
      summary: "Strong audience overlap with " + e.name + " on enterprise content ops. Best joint plays: co-authored guides and a shared webinar funnel.",
      sections: [
        { title: "Complementary topics", kind: "rows", rows: [
          { id: "c1", title: "Scaling content on managed WordPress", prio: "high", desc: "Plays to both audiences.", cap: ["topic", "blog"] },
          { id: "c2", title: "Security for content teams", prio: "medium", desc: "Their strength, your audience.", cap: ["topic"] },
        ] },
        { title: "Co-marketing angles", kind: "rows", rows: [
          { id: "a1", title: "Joint webinar: content ops at scale", desc: "Shared registration list.", cap: ["todo"] },
          { id: "a2", title: "Co-authored benchmark report", desc: "Link-building + lead-gen.", cap: ["todo", "page"] },
        ] },
        { title: "Joint keywords", kind: "rows", rows: [
          { id: "jk1", title: "managed wordpress seo", intent: "Commercial", desc: "Neither of you ranks well yet.", cap: ["keyword"] },
        ] },
      ],
      discovered: [{ id: "p1", name: "Kinsta", conf: 71, sum: "Managed host - potential complementary partner." }],
    }),
  };

  // ---------- Industry Analysis ----------
  const INDUSTRY = {
    title: "Industry Analysis", plural: "industries", noun: "analysis", icon: "globe",
    verb: "analyse", verbing: "Analysing", verbed: "analysed", reverb: "Re-analyse", batchVerb: "Analyse", biLabel: "Industry",
    intro: <>The <b>industry lens</b>. Surface segment trends, sub-segments, key players and keyword themes for an industry you operate in. Reads from <b>BI · Industry</b>.</>,
    emptyDesc: "No industries in BI yet. Add them in BI · Industry first, then analyse them here.",
    neverDesc: "Analyse this industry segment to surface trends, sub-segments, key players and keyword themes.",
    discovers: true,
    entityType: "industry", endpoint: "industry-analysis",
    mapEntity: (row) => { const d = row.data || {}; return { id: row.id, name: row.name, offer: !!row.is_ours, meta: [["Service line", d.service_line || "-"], ["Geography", d.geography || "-"]] }; },
    mapResult: (r) => ({
      summary: r.summary || "",
      sections: [
        truncSec(r),
        rowsSec("Content themes", (r.content_themes || []).map((t, i) => ({ id: "ct" + i, title: t.theme, desc: t.detail, cap: ["topic", "blog"], meta: { detail: t.detail } }))),
        rowsSec("Article titles", titleRows(r)),
        rowsSec("Wider themes", themeRows(r)),
        rowsSec("Content angles", (r.content_angles || []).map((a, i) => ({ id: "ca" + i, title: a.angle, prio: a.priority, desc: a.why, cap: ["topic"], meta: { detail: a.why, priority: a.priority } }))),
        // A cluster is one row and many keywords. Capturing the row used to
        // bank the cluster NAME as a single keyword and drop its members; each
        // example now travels as its own keyword carrying the cluster and
        // intent the analysis grouped it under, which is the strongest signal
        // plan assembly has for pulling keywords together, and the only place
        // keywords.cluster is ever written from. `bulkType` hands the whole
        // cluster to the server's keyword_cluster mapping, which fans it out;
        // `values` is the same list locally, for the tick and the untoggle.
        // A cluster with no examples captures NOTHING: its name is the name of
        // a group, not a search term the analysis said anyone types.
        rowsSec("Keyword themes", (r.keyword_clusters || []).map((k, i) => ({
          id: "kc" + i, title: k.cluster, intent: k.intent, desc: (k.examples || []).join(", "), cap: ["keyword"],
          bulkType: "keyword_cluster", emptyNote: "no examples returned for this cluster",
          meta: { cluster: k.cluster, intent: k.intent, examples: k.examples || [] },
          values: (k.examples || []).map(x => ({ value: x, meta: { cluster: k.cluster, intent: k.intent } })),
        })), { clustered: true }),
        // The FAQ bank takes a question unchanged, and these ARE the questions
        // buyers ask, so as chips they were findings nobody could bank. They
        // arrive with no answer and are captured that way: the bank's status
        // vocabulary starts at `idea` for exactly this, and writing an answer
        // here would be inventing one the analysis never gave.
        rowsSec("Audience questions", (r.audience_questions || [])
          .map(q => (typeof q === "string" ? q : (q && q.question) || "").trim())
          .filter(Boolean)
          .map((q, i) => ({ id: "aq" + i, title: q, cap: ["faq"] }))),
        chipsSec("Trends", (r.trends || []).map(t => ({ t: typeof t === "string" ? t : (t.trend || "") }))),
        rowsSec("Gaps vs us", (r.gaps_vs_us || []).map((g, i) => ({ id: "ig" + i, title: g.gap, prio: g.priority, desc: g.opportunity, cap: ["topic", "todo"], meta: { detail: g.opportunity, priority: g.priority } }))),
        proseSec("What your site already covers", covNote(r)),
        // The industry lens is the one that searches the live web and reads
        // Search Console, so it is the one that has to say whether it really
        // did: a searched answer and a remembered one look identical otherwise.
        proseSec("How this was researched", evidenceNote(r)),
      ].filter(Boolean),
      discovered: disc(r),
    }),
    stats: [["tracked", "Tracked", "var(--primary)", 5], ["ours", "We operate in", "var(--indigo-600)", 2], ["analysed", "Analysed", "var(--success)", 2]],
    statFilter: (s, e) => s === "ours" ? e.offer : s === "analysed" ? !!e.analysed : true,
    cardBadges: (e) => <>{e.offer && <span className="bi-badge bi-badge--fetched">We operate in</span>}</>,    makeResult: (e) => ({
      summary: e.name + " is consolidating around a few platform players, with rising demand for compliance-adjacent content. Two fast-growing sub-segments are under-served.",
      sections: [
        { title: "Segment trends", kind: "prose", text: "Search demand up 28% YoY, concentrated in regulatory and how-to queries. AI-assisted tooling is reshaping buyer expectations." },
        { title: "Sub-segments", kind: "chips", chips: [{ t: "Digital banking" }, { t: "Embedded finance" }, { t: "RegTech" }, { t: "Payments" }] },
        { title: "Key players", kind: "rows", rows: [
          { id: "pl1", title: "Stripe", desc: "Sets the content benchmark for developer docs.", cap: ["topic"] },
          { id: "pl2", title: "Wise", desc: "Strong on cross-border comparison content.", cap: ["topic"] },
        ] },
        { title: "Keyword themes", kind: "rows", rows: [
          { id: "kt1", title: "open a business account online", intent: "Transactional", desc: "High commercial value, rising.", cap: ["keyword"] },
          { id: "kt2", title: "vat registration france", intent: "Commercial", desc: "Steady, under-served.", cap: ["keyword"] },
        ] },
      ],
      discovered: [{ id: "i1", name: "Embedded finance", conf: 76, sum: "Fast-growing adjacent segment worth tracking." }],
    }),
  };

  // ---------- Product Enrichment (no promotion) ----------
  const PRODUCTS = {
    title: "Product Enrichment", plural: "offerings", noun: "enrichment", icon: "box",
    verb: "enrich", verbing: "Enriching", verbed: "enriched", reverb: "Re-enrich", batchVerb: "Enrich", biLabel: "Products",
    intro: <>The <b>enrichment lens</b>. Enrich a tracked offering with value props, recommended page sections, keywords and FAQ - drawn from the other providers tracked on it. Reads from <b>BI · Products</b>.</>,
    emptyDesc: "No products in BI yet. Add offerings in BI · Products first, then enrich them here.",
    neverDesc: "Enrich this offering with value propositions, page sections, keywords and FAQ, using the other providers tracked on it.",
    discovers: false,
    entityType: "product", endpoint: "product-enrichment",
    mapEntity: (row) => { const d = row.data || {}; return { id: row.id, name: row.name, offer: !!row.is_ours, meta: [["Service line", d.category || "-"], ["Pricing", d.pricing || "-"]] }; },
    mapResult: (r) => ({
      summary: r.summary || "",
      sections: [
        rowsSec("Value propositions", (r.value_propositions || []).map((v, i) => ({ id: "v" + i, title: v.point, desc: v.detail, cap: ["page"], meta: { detail: v.detail } }))),
        rowsSec("Competitive angle", (r.competitive_angle || []).map((c, i) => ({ id: "ca" + i, title: c.point, desc: c.detail, cap: ["page"], meta: { detail: c.detail } }))),
        rowsSec("Recommended page sections", (r.content_sections || []).map((s, i) => ({ id: "s" + i, title: s.section, prio: s.priority, desc: s.detail, cap: ["page"], meta: { detail: s.detail, priority: s.priority } }))),
        rowsSec("Page keywords", (r.page_keywords || []).map((k, i) => ({ id: "pk" + i, title: k.keyword, intent: k.intent, desc: k.note, cap: ["keyword"], meta: { intent: k.intent, note: k.note } }))),
        rowsSec("FAQ", (r.faq || []).map((f, i) => ({ id: "f" + i, title: f.question, desc: f.answer, cap: ["faq"], meta: { answer: f.answer } }))),
        proseSec("Pricing positioning", r.pricing_positioning),
        rowsSec("Enrichment gaps", (r.enrichment_gaps || []).map((g, i) => ({ id: "eg" + i, title: g.gap, prio: g.priority, desc: g.opportunity, cap: ["todo"], meta: { detail: g.opportunity, priority: g.priority } }))),
      ].filter(Boolean),
      discovered: [],
    }),
    stats: [["offerings", "Offerings", "var(--primary)", 5], ["offer", "We offer", "var(--success)", 2], ["gaps", "Gaps", "var(--amber-500)", 3]],
    statFilter: (s, e) => s === "offer" ? e.offer : s === "gaps" ? !e.offer : true,
    cardBadges: (e) => e.offer ? <span className="bi-badge bi-badge--validated"><I name="star" size={11} />We offer</span> : <span className="bi-badge bi-badge--alt"><I name="alert-triangle" size={11} />Gap</span>,    makeResult: (e) => ({
      summary: "Competitors position " + e.name + " around speed and fixed pricing. You can win on advisor support and bundling - and you're missing several high-intent FAQ answers.",
      sections: [
        { title: "Value propositions", kind: "rows", rows: [
          { id: "v1", title: "Fixed fee, no surprises", desc: "Matches competitor messaging; lead with it.", cap: ["page"] },
          { id: "v2", title: "Dedicated advisor included", desc: "Differentiator competitors lack.", cap: ["page"] },
        ] },
        { title: "Competitive angle", kind: "prose", text: "Both tracked competitors compete on price alone. Your bundled advisory is a defensible angle - make it the hero of the page." },
        { title: "Recommended page sections", kind: "rows", rows: [
          { id: "s1", title: "What's included & timeline", prio: "high", desc: "Top of every competitor page.", cap: ["page"] },
          { id: "s2", title: "Pricing comparison table", prio: "medium", desc: "Builds trust, captures comparison intent.", cap: ["page"] },
        ] },
        { title: "Page keywords", kind: "rows", rows: [
          { id: "pk1", title: "vat registration france cost", intent: "Commercial", desc: "High intent, you're unranked.", cap: ["keyword"] },
        ] },
        { title: "FAQ", kind: "rows", rows: [
          { id: "f1", title: "How long does VAT registration take?", desc: "Common pre-sale question.", cap: ["faq"] },
          { id: "f2", title: "Do I need a local address?", desc: "Blocks conversion if unanswered.", cap: ["faq"] },
        ] },
        { title: "Pricing positioning", kind: "prose", text: "Your €149 sits below the €180-200 competitor band - lean into value, not just the lower price." },
        { title: "Enrichment gaps", kind: "rows", rows: [
          { id: "eg1", title: "No comparison page exists yet", prio: "high", desc: "Create one to capture comparison searches.", cap: ["todo"] },
        ] },
      ],
    }),
  };

  window.MlResearchLenses = { COMPETITORS, PARTNERS, INDUSTRY, PRODUCTS };
})();
